// src/contexts/LanguageContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import translations from "../i18n/translations";

/**
 * LA LANGUE DU TABLEAU DE BORD.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * CE CONTEXTE EXISTAIT DÉJÀ, ET IL NE TRADUISAIT RIEN. Les dix-huit dashboards l'importent
 * et affichent un bascule 🇫🇷 FR / 🇬🇧 EN à dix-sept endroits — mais ils n'en lisaient que
 * `lang`, pour choisir le drapeau. Aucun appel à `t()` dans un seul d'entre eux : les
 * 536 clés de `i18n/translations.js` ne servaient qu'aux trois pages de connexion. Cliquer
 * sur « EN » changeait donc le drapeau, et pas un mot de l'interface.
 *
 * POURQUOI LE CONTEXTE EST CONSERVÉ plutôt que remplacé : les dix-sept bascules passent
 * toutes par lui. En rebranchant ce seul fichier, les quinze autres se mettent à fonctionner
 * sans être modifiés — et sans toucher à des dashboards de vingt mille lignes.
 *
 * C'EST `TraducteurDePage` QUI TRADUIT, en lisant `lang` ici. Il parcourt le texte affiché
 * et le fait traduire par `/api/traduire`, donc par le cache DeepL du Backend. Le dash pèse
 * 43 499 mots derrière un identifiant : un abonnement au mot y coûterait cher pour un espace
 * qu'aucun moteur de recherche ne lira. Le site public, lui, reste sur Weglot.
 *
 * `t()` REND TOUJOURS LE FRANÇAIS, et c'est délibéré. Le traducteur lit le texte AFFICHÉ :
 * si `t()` avait déjà rendu de l'anglais sur les pages de connexion, il traduirait de
 * l'anglais vers l'anglais. Les clés restent utilisées comme source française.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  /* LE CHOIX EST GARDÉ D'UNE SESSION À L'AUTRE. Quelqu'un qui travaille en anglais ne doit
     pas le redemander à chaque connexion. */
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("bet_lang") === "EN" ? "EN" : "FR"; }
    catch { return "FR"; }
  });

  const toggleLang = useCallback(() => {
    setLang((prec) => {
      const suivante = prec === "FR" ? "EN" : "FR";
      try { localStorage.setItem("bet_lang", suivante); } catch { /* vaudra pour cette session */ }
      return suivante;
    });
  }, []);

  /**
   * Le libellé d'une clé, dans la langue courante.
   *
   * `t()` AVAIT ÉTÉ FIGÉ SUR LE FRANÇAIS pour éviter que le traducteur de page ne retraduise
   * de l'anglais déjà rendu. La précaution était inutile : les quatre sections `login*` —
   * 183 clés, toutes appariées FR/EN — ne servent qu'aux pages de CONNEXION, et le
   * traducteur ne tourne qu'une fois la session ouverte. Les deux ne se croisent jamais.
   *
   * Les laisser bilingues rend donc la bascule utile AVANT connexion, là où le traducteur ne
   * peut rien faire faute de session — et avec des traductions écrites à la main, meilleures
   * que ce qu'une API rendrait.
   */
  const t = useCallback((section, key) => {
    return translations[section]?.[lang]?.[key]
        ?? translations[section]?.["FR"]?.[key]
        ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}
